import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  usePageMeta({
    title: 'Privacy Policy - Monthly Wealth Tracker',
    description: 'Read how Monthly Wealth Tracker collects, uses, and protects your data.',
    path: '/privacy',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-slate-900">
      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-lg p-8 sm:p-12">
          <h1 className="text-4xl font-bold text-gray-100 mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Welcome to Monthly Wealth Tracker ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data.
                This privacy policy explains how we collect, use, and safeguard your information when you use our application.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">2. Information We Collect</h2>
              <p className="leading-relaxed mb-3">When you use Monthly Wealth Tracker, we collect the following information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google Account Information:</strong> Your name, email address, and profile picture when you sign in with Google</li>
                <li><strong>Financial Data:</strong> The asset and debt information you voluntarily enter into the application</li>
                <li><strong>Usage Data:</strong> Information about how you interact with our application</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-3">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain our service</li>
                <li>Authenticate your identity via Google OAuth</li>
                <li>Store your financial tracking data securely</li>
                <li>Sync your data across devices</li>
                <li>Improve our application and user experience</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">4. Data Storage and Security</h2>
              <p className="leading-relaxed mb-3">
                Your data is stored securely using Google Firebase services. We implement appropriate technical and organizational measures to protect your personal data, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication via Google OAuth 2.0</li>
                <li>Access controls to limit data access to authorized users only</li>
                <li>Regular security assessments and updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">5. Data Sharing and Disclosure</h2>
              <p className="leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We do not share your financial data with anyone.
                Your data is private and belongs to you. We may only disclose your information if required by law or to protect our rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">6. Your Rights</h2>
              <p className="leading-relaxed mb-3">You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal data stored in our application</li>
                <li>Correct or update your information at any time</li>
                <li>Delete your account and all associated data</li>
                <li>Export your data in a portable format</li>
                <li>Revoke Google OAuth access at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">7. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your data for as long as your account is active. If you delete your account, all your data will be permanently deleted
                from our systems within 30 days. You can request immediate data deletion by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">8. Google OAuth and Third-Party Services</h2>
              <p className="leading-relaxed mb-3">
                We use Google OAuth for authentication. When you sign in with Google, you are also subject to Google's Privacy Policy.
                We only request the minimum necessary permissions (profile and email) to provide our service.
              </p>
              <p className="leading-relaxed">
                Google Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline">https://policies.google.com/privacy</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">9. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
                If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">10. Changes to This Privacy Policy</h2>
              <p className="leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page
                and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-100 mb-4">11. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
                <p className="font-medium">Email: <a href="mailto:privacy@monthlywealthtracker.com" className="text-indigo-400 hover:underline">privacy@monthlywealthtracker.com</a></p>
              </div>
            </section>

            <section className="border-t border-gray-700 pt-8 mt-8">
              <p className="text-sm text-gray-400">
                By using Monthly Wealth Tracker, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
